import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { countries } from '../data/countries';
import './AdvertiserForm.css';

interface AffiliateFormData {
  name: string;
  email: string;
  country: string;
  postback: string;
  status: 'pending' | 'approved' | 'blocked';
  tier: 'standard' | 'premium' | 'sensitive';
  postbackPercentage: string;
  postbackStatus: 'start' | 'pause';
  timezone: string;
  password: string;
}

const AddAffiliate: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<AffiliateFormData>({
    name: '',
    email: '',
    country: '',
    postback: '',
    status: 'pending',
    tier: 'standard',
    postbackPercentage: '100',
    postbackStatus: 'start',
    timezone: '',
    password: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Country dropdown state
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState<string[]>(countries);
  const countryInputRef = useRef<HTMLInputElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCountrySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const exactCountry = countries.find(country => country.toLocaleLowerCase('en-US') === value.trim().toLocaleLowerCase('en-US'));
    setCountrySearch(value);
    setFormData(prev => ({ ...prev, country: exactCountry || '' }));
    setErrors(prev => ({ ...prev, country: '' }));
    setShowCountryDropdown(true);

    // Filter countries based on search term
    const filtered = countries.filter(country =>
      country.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCountries(filtered);
  };

  const handleCountrySelect = (country: string) => {
    setFormData(prev => ({ ...prev, country }));
    setCountrySearch(country);
    setShowCountryDropdown(false);
    // Clear error when country is selected
    if (errors.country) {
      setErrors(prev => ({
        ...prev,
        country: ''
      }));
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node) &&
        countryInputRef.current &&
        !countryInputRef.current.contains(event.target as Node)
      ) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync countrySearch with formData.country
  useEffect(() => {
    if (formData.country) {
      setCountrySearch(formData.country);
    }
  }, [formData.country]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    if (!formData.postback.trim()) {
      newErrors.postback = 'Postback URL is required';
    } else if (!/^https?:\/\/.+/.test(formData.postback)) {
      newErrors.postback = 'Postback URL must be a valid URL';
    }

    if (!formData.timezone.trim()) {
      newErrors.timezone = 'Timezone is required';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.trim().length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    if (validateForm()) {
      setLoading(true);

      try {
        // Map form data to API format
        const apiData = {
          name: formData.name,
          email: formData.email,
          country: formData.country,
          callback_template: formData.postback,
          status: formData.status,
          tier: formData.tier,
          postback_percentage: parseInt(formData.postbackPercentage),
          postback_status: formData.postbackStatus,
          timezone: formData.timezone,
          password: formData.password // Required field
        };

        await apiService.createPublisher(apiData);

        // Success - navigate back to affiliates list
        navigate('/affiliates');
      } catch (err: any) {
        setApiError(err.message || 'Failed to create affiliate');
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    navigate('/affiliates');
  };

  return (
    <div className="advertiser-form-page">
      <div className="page-header">
        <h1>Add New Affiliate</h1>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="advertiser-form">
          <div className="form-section">
            <h2>Basic Information</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? 'error' : ''}
                  placeholder="Enter affiliate name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="Enter email address"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ position: 'relative' }}>
                <label htmlFor="country">Country *</label>
                <input
                  ref={countryInputRef}
                  type="text"
                  id="country"
                  name="country"
                  value={countrySearch}
                  onChange={handleCountrySearch}
                  onFocus={() => setShowCountryDropdown(true)}
                  className={errors.country ? 'error' : ''}
                  placeholder="Type to search countries..."
                  autoComplete="off"
                />
                {showCountryDropdown && filteredCountries.length > 0 && (
                  <div
                    ref={countryDropdownRef}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      backgroundColor: '#4a5568',
                      border: '1px solid #5a6c7d',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      zIndex: 1000,
                      marginTop: '4px'
                    }}
                  >
                    {filteredCountries.map((country) => (
                      <div
                        key={country}
                        onClick={() => handleCountrySelect(country)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #5a6c7d',
                          color: '#ecf0f1',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3c4855'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {country}
                      </div>
                    ))}
                  </div>
                )}
                {errors.country && <span className="error-message">{errors.country}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="timezone">Timezone *</label>
                <select
                  id="timezone"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleInputChange}
                  className={errors.timezone ? 'error' : ''}
                >
                  <option value="">Select Timezone</option>
                  <option value="Asia/Kolkata">Asia/Kolkata</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="America/Toronto">America/Toronto</option>
                  <option value="Australia/Sydney">Australia/Sydney</option>
                  <option value="Asia/Dubai">Asia/Dubai</option>
                  <option value="Asia/Hong_Kong">Asia/Hong_Kong</option>
                  <option value="Asia/Singapore">Asia/Singapore</option>
                  <option value="Europe/Kiev">Europe/Kiev</option>
                </select>
                {errors.timezone && <span className="error-message">{errors.timezone}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="tier">Publisher Tier</label>
                <select
                  id="tier"
                  name="tier"
                  value={formData.tier}
                  onChange={handleInputChange}
                >
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="sensitive">Sensitive</option>
                </select>
                <small className="field-hint">Tier affects payout calculations</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="postbackPercentage">Postback Percentage</label>
                <select
                  id="postbackPercentage"
                  name="postbackPercentage"
                  value={formData.postbackPercentage}
                  onChange={handleInputChange}
                >
                  <option value="100">100% - All conversions</option>
                  <option value="80">80% - 8 out of 10</option>
                  <option value="60">60% - 6 out of 10</option>
                  <option value="40">40% - 4 out of 10</option>
                  <option value="20">20% - 2 out of 10</option>
                  <option value="10">10% - 1 out of 10</option>
                  <option value="5">5% - 1 out of 20</option>
                </select>
                <small className="field-hint">Controls how many conversion postbacks are fired to publisher</small>
              </div>

              <div className="form-group">
                <label htmlFor="postbackStatus">Postback Hit</label>
                <select
                  id="postbackStatus"
                  name="postbackStatus"
                  value={formData.postbackStatus}
                  onChange={handleInputChange}
                >
                  <option value="start">Start - Fire postbacks</option>
                  <option value="pause">Pause - No postbacks</option>
                </select>
                <small className="field-hint">Control whether postbacks are fired for this publisher</small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Login Credentials</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.email}
                  className="form-input"
                  placeholder="Email will be used as username"
                  readOnly
                  style={{ backgroundColor: '#525f6e', cursor: 'not-allowed' }}
                />
                <small className="field-hint">Username is automatically set to the email address</small>
              </div>

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={errors.password ? 'form-input error' : 'form-input'}
                  placeholder="Enter password (min 6 characters)"
                  required
                />
                {errors.password && <span className="error-message">{errors.password}</span>}
                <small className="field-hint">Set a secure password for publisher login (minimum 6 characters)</small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Postback Configuration</h2>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="postback">Postback URL *</label>
                <input
                  type="text"
                  id="postback"
                  name="postback"
                  value={formData.postback}
                  onChange={handleInputChange}
                  className={errors.postback ? 'error' : ''}
                  placeholder="https://example.com/postback?click_id={aff_sub}"
                />
                {errors.postback && <span className="error-message">{errors.postback}</span>}
                <small className="field-hint">
                  Use macros: {'{aff_sub}'} for click ID, {'{payout}'} for payout amount
                </small>
              </div>
            </div>
          </div>

          {apiError && (
            <div className="api-error-banner" role="alert">
              <strong>Error:</strong> {apiError}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Affiliate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAffiliate;
